// PROXY_CONTEST — the fourth registry entry, and the first whose approve
// action is a genuine state-machine transition rather than a one-shot
// approve-and-done. A contest job cycles through Not Sent → Sent →
// (Signed by Dissident | Signed by Solicitor → re-route) → Received → Sent to
// Discovery over real elapsed time (days between stages), so the task simply
// stays in the Intake Queue — re-opened as a fresh session each time — until
// it reaches the terminal SENT_TO_DISCOVERY state. All the state-machine
// rules are pure, already-tested logic this module just wires to the
// registry contract: utils/proxyContestState.ts's transitionFormStatus()/
// nextActionsFor() (the signature state machine + the solicitor-signature-
// always-rejected rule) and isFeeDiscounted() (below-standard-fee flag).
import type { ProxyJob, ContestFormStatus } from '../mockProxyJobs';
import { PROXY_SEED_TODAY } from '../mockProxyJobs';
import type { ProxyInboxEmail } from '../mockProxyEmails';
import { classifyEmail } from '../../utils/proxyEmailClassifier';
import {
  transitionFormStatus, nextActionsFor, FORM_STATUS_LABEL, STANDARD_CONTEST_FEE, isFeeDiscounted,
  type ContestFormEvent,
} from '../../utils/proxyContestState';
import { resolveOutreachContact, FALLBACK_SOLICITORS } from '../proxyFallbackSolicitors';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition,
  ProxyExtractedField, ProxyCalcRow, ProxyReviewPanelSchema, ProxyPanelContext,
} from '../proxyWorkflowTypes';

const CONTEST_REASON: Record<ContestFormStatus, string> = {
  NOT_SENT: 'Discovery was notified of a new proxy contest. Outreach hasn\'t been sent yet.',
  SENT: 'Contest outreach sent. Waiting for a signed form back.',
  SIGNED_SOLICITOR_REJECTED: 'Solicitor signature is invalid, so the form needs to be re-routed to the solicitor for the dissident\'s signature.',
  SIGNED_DISSIDENT: 'Valid dissident signature logged. Waiting for the physical/scanned form.',
  RECEIVED: 'Signed form received and ready to send to Discovery for BOC upload.',
  SENT_TO_DISCOVERY: 'Sent to Discovery and closed.',
};

function findContestEmail(job: ProxyJob, emails: ProxyInboxEmail[]): ProxyInboxEmail | undefined {
  return emails.find(e => e.relatedJobId === job.jobId && classifyEmail(e).category === 'PROXY_CONTEST');
}

// ICT-3: the party won't send material to Mediant or won't pay — Discovery
// aborts the job.
const DECLINED_REASON = 'The party says they won\'t send material to Mediant or won\'t pay. Ask Discovery to abort the job.';
function emailDeclines(email: ProxyInboxEmail | undefined): boolean {
  return !!email && /will not be sending|won't be sending|will not pay|won't pay|refuse|decline/i.test(email.body);
}

// The next single transition for the job's CURRENT status — SENT is the only
// status with a real branch (which party signed), driven by edits.signedBy;
// every other status has exactly one next action.
function resolveNextEvent(status: ContestFormStatus, signedBy: 'dissident' | 'solicitor'): ContestFormEvent {
  if (status === 'SENT') return { type: 'SIGNATURE_RECEIVED', signedBy };
  const actions = nextActionsFor(status);
  return actions[0]?.event ?? { type: 'SEND' };
}

export function buildContestTask(job: ProxyJob, emails: ProxyInboxEmail[]): ProxyTask {
  const contest = job.contest!;
  const email = findContestEmail(job, emails);
  const reason = contest.formStatus === 'SENT' && emailDeclines(email) ? DECLINED_REASON : CONTEST_REASON[contest.formStatus];
  return {
    id: `contest-${job.jobId}`,
    workflowType: 'PROXY_CONTEST',
    jobId: job.jobId,
    title: `${job.issuer} - Proxy contest`,
    subtitle: `Job ${job.jobId}`,
    reason,
    source: email ? 'email' : 'job_signal',
    sourceEmailId: email?.id,
    sourceLabel: email ? undefined : 'contest status',
    sourceDetail: email ? undefined : {
      rows: [
        { label: 'Dissident party', value: contest.dissidentParty },
        { label: 'Form status', value: FORM_STATUS_LABEL[contest.formStatus] },
        { label: 'Attempts', value: `${contest.attempts}/3` },
      ],
    },
    createdAt: email?.date || PROXY_SEED_TODAY,
    confidence: email ? Math.round(classifyEmail(email).confidence * 100) : 90,
  };
}

function buildTasks({ jobs, emails }: ProxyWorkflowContext): ProxyTask[] {
  return jobs
    .filter(job => job.contest && job.contest.formStatus !== 'SENT_TO_DISCOVERY' && job.status !== 'ABORTED')
    .map(job => buildContestTask(job, emails));
}

function defaultEdits(task: ProxyTask, job: ProxyJob): Record<string, any> {
  return {
    signedBy: 'dissident',
    fee: job.contest?.fee ?? STANDARD_CONTEST_FEE,
    fallbackSolicitor: FALLBACK_SOLICITORS[0].name,
  };
}

function isDeclined(job: ProxyJob, edits: Record<string, any>, email: ProxyInboxEmail | undefined): boolean {
  return job.contest!.formStatus === 'SENT' && (edits.response ?? (emailDeclines(email) ? 'declined' : 'returned')) === 'declined';
}

function buildPanel({ job, edits, email }: ProxyPanelContext): ProxyReviewPanelSchema {
  const contest = job.contest!;
  const status = contest.formStatus;
  const declined = isDeclined(job, edits, email);
  const signedBy: 'dissident' | 'solicitor' = edits.signedBy ?? 'dissident';
  const event = resolveNextEvent(status, signedBy);
  const transition = transitionFormStatus(status, event);
  const fee = edits.fee ?? contest.fee;
  const feeDiscounted = isFeeDiscounted(fee);

  const fallbackContact = FALLBACK_SOLICITORS.find(s => s.name === edits.fallbackSolicitor) || FALLBACK_SOLICITORS[0];
  const known = resolveOutreachContact(job);
  const recipient = known
    ? { name: known.name, email: known.email, isFallback: false }
    : { name: fallbackContact.name, email: fallbackContact.email, isFallback: true };

  const extractedFields: ProxyExtractedField[] = [
    { key: 'issuer', label: 'Issuer', display: job.issuer },
    { key: 'jobId', label: 'Job #', display: job.jobId },
    { key: 'cusip', label: 'CUSIP', display: job.cusip },
    { key: 'dissidentParty', label: 'Dissident party', display: contest.dissidentParty },
    { key: 'formStatus', label: 'Form status', display: FORM_STATUS_LABEL[status] },
    { key: 'attempts', label: 'Attempts', display: `${contest.attempts}/3` },
    { key: 'fee', label: 'Fee', display: `$${fee.toLocaleString()}`, editable: { kind: 'number', value: fee } },
  ];
  if (!known) {
    extractedFields.push({
      key: 'fallbackSolicitor', label: 'Route via (no solicitor on record)',
      display: fallbackContact.name,
      editable: { kind: 'enum', value: fallbackContact.name, options: FALLBACK_SOLICITORS.map(s => ({ value: s.name, label: s.name })) },
    });
  }
  if (status === 'SENT') {
    extractedFields.push({
      key: 'response', label: 'Response (Ace read)',
      display: declined ? 'Declined: won\'t send material or pay' : 'Form returned',
      editable: { kind: 'enum', value: declined ? 'declined' : 'returned', options: [{ value: 'returned', label: 'Form returned' }, { value: 'declined', label: 'Declined: won\'t send material or pay' }] },
    });
  }
  if (status === 'SENT' && !declined) {
    extractedFields.push({
      key: 'signedBy', label: 'Signed by',
      display: signedBy === 'dissident' ? 'Dissident' : 'Solicitor',
      editable: { kind: 'enum', value: signedBy, options: [{ value: 'dissident', label: 'Dissident' }, { value: 'solicitor', label: 'Solicitor' }] },
    });
  }

  const rows: ProxyCalcRow[] = [
    { label: 'Current status', value: FORM_STATUS_LABEL[status] },
    { label: 'Next step', value: declined ? DECLINED_REASON : transition.reason },
  ];
  if (feeDiscounted) {
    rows.push({ label: 'Fee', value: `Escalate to a manager. $${fee.toLocaleString()} is below the $${STANDARD_CONTEST_FEE.toLocaleString()} standard fee.`, tone: 'amber' });
  }
  if (contest.attempts >= 3 && event.type === 'SEND') {
    rows.push({ label: 'Attempts', value: `Escalate to a manager. ${contest.attempts} of 3 attempts are already used.`, tone: 'amber' });
  }

  let drafts: ProxyReviewPanelSchema['drafts'] = [];
  let outboundActions: ProxyReviewPanelSchema['outboundActions'] = [];
  let approveSummary: string[] = [];
  let approveConfirmation = '';

  if (declined) {
    const subject = `Please abort - ${job.issuer} (Job ${job.jobId})`;
    const body = `Hello,\n\nPlease abort this proxy contest job. ${recipient.name} says the dissident party (${contest.dissidentParty}) will not send material to Mediant for distribution or pay for the service.${email ? `\n\n${email.from} wrote:\n"${email.body}"` : ''}\n\nJob #: ${job.jobId}\nCUSIP: ${job.cusip}\nIssuer: ${job.issuer}\n\nThank you,\nICT / Collections`;
    drafts = [{ key: 'abort', icon: '📧', label: 'Abort request → Discovery', to: 'documents@betanxt.com', subject, body }];
    outboundActions = [
      { key: 'abort', followUpType: 'contest_abort_requested', recipientEmail: 'documents@betanxt.com', subject, bodyPreview: body },
      { key: 'mic', followUpType: 'contest_declined', recipientEmail: 'mic@betanxt.com', subject: `Contest declined for Job ${job.jobId}`, bodyPreview: `${recipient.name}: dissident won't send material or pay. Discovery asked to abort.` },
    ];
    approveSummary = ['Email Discovery to abort the job, with the reason', 'Document the Materials tab in MIC', 'Close the contest with no more chases.'];
    approveConfirmation = `Abort requested from Discovery for Job ${job.jobId} because the dissident won't send material or pay.`;
  } else if (event.type === 'SEND') {
    const subject = `Proxy Contest Outreach - ${job.issuer} (Job ${job.jobId})`;
    const body = `Contest response form attached for the above job (dissident: ${contest.dissidentParty}). Fee: $${fee.toLocaleString()}. Please return the signed form to proceed.`;
    drafts = [{
      key: 'outreach', icon: '📧', label: `Contest outreach → ${recipient.name}`,
      to: recipient.email, cc: ['proxymaterial@betanxt.com'],
      note: recipient.isFallback ? 'fallback contact, no solicitor on record' : undefined,
      subject, body,
    }];
    outboundActions = [{ key: 'outreach', followUpType: 'contest_outreach', recipientEmail: recipient.email, ccEmails: ['proxymaterial@betanxt.com'], subject, bodyPreview: body }];
    approveSummary = ['Send the contest outreach email (form attached, proxymaterial@ cc\'d)', 'Move this task to Done. It reopens once a signed form comes back.'];
    approveConfirmation = `Contest outreach sent to <strong>${recipient.name}</strong> for Job ${job.jobId}.`;
  } else if (event.type === 'SIGNATURE_RECEIVED') {
    const subject = signedBy === 'dissident'
      ? `Valid signature received from the dissident for Job ${job.jobId}`
      : `Invalid signature received from the solicitor for Job ${job.jobId}`;
    outboundActions = [{ key: 'signature', followUpType: 'signature_event', recipientEmail: recipient.email, subject, bodyPreview: transition.reason }];
    approveSummary = signedBy === 'dissident'
      ? ['Log the valid dissident signature', 'Move this task to Done. It reopens once the physical form is received.']
      : ['Log the invalid solicitor signature', 'Re-route the form to the solicitor for dissident signature', 'Move this task to Done. It reopens once the form is re-sent.'];
    approveConfirmation = signedBy === 'dissident'
      ? `Dissident signature logged for Job ${job.jobId}.`
      : `Solicitor signature rejected and re-routed for Job ${job.jobId}.`;
  } else if (event.type === 'MARK_RECEIVED') {
    outboundActions = [{ key: 'received', followUpType: 'contest_form_received', recipientEmail: 'mic@betanxt.com', subject: `Signed contest form received for Job ${job.jobId}`, bodyPreview: transition.reason }];
    approveSummary = ['Log the signed form as received', 'Move this task to Done. It reopens once it\'s ready to send to Discovery.'];
    approveConfirmation = `Signed contest form logged as received for Job ${job.jobId}.`;
  } else if (event.type === 'SEND_TO_DISCOVERY') {
    const subject = `Discovery Hand-off - ${job.issuer}`;
    const body = 'Signed contest form + billing info sent to Discovery for BOC upload.';
    outboundActions = [{ key: 'discovery', followUpType: 'discovery_handoff', recipientEmail: 'discovery@betanxt.com', subject, bodyPreview: body }];
    approveSummary = ['Send the signed form + billing info to Discovery for BOC upload', 'Capture billing', 'Move this task to Done.'];
    approveConfirmation = `Signed form sent to Discovery and billing captured for Job ${job.jobId}.`;
  }

  return {
    extractedFields,
    calculation: {
      title: 'Contest state machine',
      ruleText: transition.reason,
      rows,
      totalRow: { label: 'Attempts used', value: `${contest.attempts}/3` },
    },
    drafts,
    approveSummary,
    approveConfirmation,
    outboundActions,
    closesTicket: declined || event.type === 'SEND_TO_DISCOVERY',
    escalation: declined ? undefined
      : feeDiscounted ? `The $${fee.toLocaleString()} fee is below the $${STANDARD_CONTEST_FEE.toLocaleString()} standard, so the manager approves it (ICT-3).`
        : contest.attempts >= 3 && event.type === 'SEND' ? 'Three chases have gone unanswered, so the manager decides what happens next (ICT-3).' : undefined,
  };
}

// The one real state-machine mutation on approve — everything else workflows
// need is already covered by outboundActions' followUpHistory writes.
function onApprove({ job, edits, email }: ProxyPanelContext): Partial<ProxyJob> {
  // Declined: the contest is over — the abort itself is Discovery's step.
  if (isDeclined(job, edits, email)) return { status: 'ABORTED' };
  const contest = job.contest!;
  const signedBy: 'dissident' | 'solicitor' = edits.signedBy ?? 'dissident';
  const event = resolveNextEvent(contest.formStatus, signedBy);
  const transition = transitionFormStatus(contest.formStatus, event);
  const nextContest = { ...contest, formStatus: transition.next };
  if (event.type === 'SEND') nextContest.attempts = contest.attempts + 1;
  if (event.type === 'SEND_TO_DISCOVERY') nextContest.billingStatus = 'CAPTURED';
  return { contest: nextContest };
}

export const PROXY_CONTEST_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'PROXY_CONTEST',
  label: 'Proxy Contest',
  noun: 'a proxy contest action',
  trigger: 'Discovery notifies of a new proxy contest (email), or an existing contest job is mid-flow through its signature state machine.',
  source: 'Inbound email (classified PROXY_CONTEST) when one exists in the inbox, else the job\'s own contest state.',
  steps: ['Source & extraction', 'Contest state machine', 'Draft & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
  onApprove,
};
